// GA4 ecommerce → GTM dataLayer. Config is injected from gtm-scripts.liquid.

if (!window.gtmConfig?.enabled) {
  console.log('GTM events disabled');
} else {
  const cfg = window.gtmConfig;
  const dl = window.dataLayer || (window.dataLayer = []);

  function debug(event, data) {
    if (cfg.debugMode) console.log(`[GTM] ${event}:`, data);
  }

  function clearEcommerce() {
    dl.push({ ecommerce: null });
  }

  function push(payload) {
    dl.push(payload);
    debug(payload.event, payload);
  }

  function money(val) {
    return parseFloat(val) || 0;
  }

  function gaItem(item, extra = {}) {
    return {
      item_id: String(item.item_id),
      item_name: item.item_name || '',
      item_brand: item.item_brand || '',
      item_category: item.item_category || '',
      item_variant: item.item_variant || '',
      price: money(item.price),
      ...extra,
    };
  }

  // --- cart errors ---

  const seenCartErrors = new Set();

  function isVisible(el) {
    if (!(el instanceof Element)) return false;
    if (typeof el.checkVisibility === 'function') {
      return el.checkVisibility({ checkOpacity: true, checkVisibilityCSS: true });
    }
    const node = /** @type {HTMLElement} */ (el);
    return Boolean(node.offsetParent || el.getClientRects?.().length);
  }

  function errorText(el) {
    const node = /** @type {HTMLElement} */ (el);
    return (node.innerText || node.textContent || '').trim();
  }

  function productErrorRoot() {
    if (cfg.pageType === 'cart') {
      return document.querySelector('.cart-page__products-list');
    }
    return document.querySelector('cart-drawer-component.cart-drawer');
  }

  function pushCartError(name, message) {
    const msg = String(message || '').trim();
    if (!msg) return;
    push({ event: 'cart_error', error_name: name, error_message: msg });
  }

  function trackCartError(name, message, visibleNow) {
    const msg = String(message || '').trim();
    if (!msg) return;

    const key = `${name}::${msg}`;
    visibleNow.add(key);
    if (seenCartErrors.has(key)) return;

    seenCartErrors.add(key);
    pushCartError(name, msg);
  }

  function scanCartErrors(root = document) {
    if (cfg.cartError === false) return;

    const scope = root instanceof Document ? document.body : root;
    if (!scope) return;

    const visibleNow = new Set();

    scope
      .querySelectorAll('.message.error-form .message-content, .error-form.message .message-content')
      .forEach((node) => {
        if (!isVisible(node)) return;
        const text = errorText(node);
        if (text) trackCartError('Overall Error', text, visibleNow);
      });

    scope.querySelectorAll('.cart-discount__error-text:not(.hidden)').forEach((node) => {
      if (!isVisible(node)) return;
      const text = errorText(node);
      if (text) trackCartError('Coupon Error', text, visibleNow);
    });

    const productRoot = productErrorRoot();
    if (productRoot) {
      productRoot.querySelectorAll('.cart-page__item-error').forEach((node) => {
        if (!isVisible(node)) return;
        const text = errorText(node);
        if (text) trackCartError('Product Error', text, visibleNow);
      });

      productRoot.querySelectorAll('td.cart-items__error:not(.hidden)').forEach((cell) => {
        if (!isVisible(cell)) return;
        const textEl = cell.querySelector('.cart-item__error-text');
        const text = textEl ? errorText(textEl) : errorText(cell);
        if (text) trackCartError('Product Error', text, visibleNow);
      });

      productRoot.querySelectorAll('.producterror').forEach((node) => {
        if (!isVisible(node)) return;
        const text = errorText(node);
        if (text) trackCartError('Product Error', text, visibleNow);
      });
    }

    // reset keys when the error disappears so it can fire again on re-show
    for (const key of seenCartErrors) {
      if (!visibleNow.has(key)) seenCartErrors.delete(key);
    }
  }

  function bindCartErrorCapture() {
    if (cfg.cartError === false) return;

    const run = () => requestAnimationFrame(() => scanCartErrors());

    document.addEventListener('cart-drawer:open', run, { passive: true });
    document.addEventListener('cart:update', run, { passive: true });
    document.addEventListener('cart:error', run, { passive: true });

    const targets = [
      document.querySelector('.cart-page__products-list'),
      document.querySelector('cart-drawer-component.cart-drawer'),
      document.querySelector('cart-items-component'),
    ].filter(Boolean);

    if (targets.length) {
      const observer = new MutationObserver(run);
      for (const target of targets) {
        observer.observe(target, {
          childList: true,
          subtree: true,
          characterData: true,
          attributes: true,
          attributeFilter: ['class', 'hidden'],
        });
      }
    }

    run();
  }

  // --- page events ---

  function viewItem() {
    if (!cfg.viewItem || cfg.pageType !== 'product' || !cfg.product) return;

    const product = cfg.product;
    const currency = cfg.currency || 'USD';

    clearEcommerce();
    push({
      event: 'view_item',
      masterProductId: String(product.masterProductId),
      item_image_url: product.item_image_url || '',
      ecommerce: {
        currency,
        value: money(product.price),
        items: [
          gaItem(product, {
            item_image_url: product.item_image_url || '',
            quantity: 1,
          }),
        ],
      },
    });
  }

  function viewItemList() {
    if (!cfg.viewItemList || cfg.pageType !== 'collection' || !cfg.collection) return;

    const collection = cfg.collection;
    const currency = cfg.currency || 'USD';

    clearEcommerce();
    push({
      event: 'view_item_list',
      ecomm_pagetype: 'category',
      ecomm_category: collection.title,
      categoryID: String(collection.id),
      categoryName: collection.title,
      categoryimageURL: collection.imageUrl || '',
      ecommerce: {
        currency,
        item_list_id: String(collection.id),
        item_list_name: collection.title,
        items: collection.items.map((item, i) =>
          gaItem(item, {
            item_category: item.item_category || collection.title,
            item_list_id: String(collection.id),
            item_list_name: collection.title,
            index: item.index || i + 1,
          })
        ),
      },
    });
  }

  function viewCart() {
    if (!cfg.viewCart || cfg.pageType !== 'cart' || !cfg.cart) return;

    const cart = cfg.cart;
    const currency = cfg.currency || 'USD';

    clearEcommerce();
    push({
      event: 'view_cart',
      ecommerce: {
        currency,
        value: money(cart.value),
        items: cart.items.map((item, i) =>
          gaItem(item, {
            quantity: item.quantity || 1,
            index: i + 1,
          })
        ),
      },
    });
  }

  function trackAddToCart(product) {
    if (!cfg.addToCart) return;

    const currency = product.currency || cfg.currency || 'USD';
    const qty = product.quantity || 1;
    const price = money(product.price);

    clearEcommerce();
    push({
      event: 'add_to_cart',
      masterProductId: String(product.masterProductId || product.item_id),
      item_image_url: product.item_image_url || '',
      ecommerce: {
        currency,
        value: price * qty,
        items: [
          gaItem(product, {
            item_image_url: product.item_image_url || '',
            quantity: qty,
          }),
        ],
      },
    });
  }

  function trackRemoveFromCart(product) {
    if (!cfg.addToCart) return;

    const currency = product.currency || cfg.currency || 'USD';
    const qty = product.quantity || 1;
    const price = money(product.price);

    clearEcommerce();
    push({
      event: 'remove_from_cart',
      ecommerce: {
        currency,
        value: price * qty,
        items: [gaItem(product, { quantity: qty })],
      },
    });
  }

  function trackPurchase(order) {
    if (!cfg.purchaseEvent) return;

    const currency = order.currency || cfg.currency || 'USD';

    clearEcommerce();
    push({
      event: 'purchase',
      ecommerce: {
        transaction_id: String(order.transaction_id),
        value: money(order.value),
        currency,
        tax: money(order.tax),
        shipping: money(order.shipping),
        coupon: order.coupon || '',
        items: (order.items || []).map((item, i) =>
          gaItem(
            {
              item_id: item.item_id || item.id,
              item_name: item.item_name || item.name,
              item_brand: item.item_brand || item.brand,
              item_category: item.item_category || item.category,
              item_variant: item.item_variant || item.variant,
              price: item.price,
            },
            { quantity: item.quantity || 1, index: i + 1 }
          )
        ),
      },
    });
  }

  function trackBanner(type, banner) {
    if (!cfg.bannerTracking) return;
    push({
      event: `banner_${type}`,
      banner_id: banner.id || '',
      banner_name: banner.name || '',
      banner_position: banner.position || '',
      banner_url: banner.url || '',
    });
  }

  function trackBid(bid) {
    if (!cfg.bidEvent) return;
    push({
      event: 'bid_event',
      bid_id: bid.id || '',
      bid_timestamp: bid.timestamp || Date.now(),
    });
  }

  function trackSelectItem(product, listId = '', listName = '') {
    if (!cfg.viewItemList) return;

    const currency = product.currency || cfg.currency || 'USD';

    clearEcommerce();
    push({
      event: 'select_item',
      ecommerce: {
        currency,
        item_list_id: listId,
        item_list_name: listName,
        items: [
          gaItem(product, {
            item_list_id: listId,
            item_list_name: listName,
            index: product.index || 1,
          }),
        ],
      },
    });
  }

  function onVariantChange(variant) {
    if (!cfg.viewItem || cfg.pageType !== 'product' || !variant) return;

    const product = cfg.product;
    if (!product) return;

    const currency = cfg.currency || 'USD';
    const price = variant.price ? variant.price / 100 : money(product.price);
    const image =
      variant.featured_media?.preview_image?.src ||
      variant.featured_image?.src ||
      product.item_image_url ||
      '';
    const variantTitle = variant.title || variant.name || variant.option1 || '';

    clearEcommerce();
    push({
      event: 'view_item',
      masterProductId: String(product.masterProductId),
      item_image_url: image,
      ecommerce: {
        currency,
        value: price,
        items: [
          gaItem(
            {
              item_id: variant.sku || String(variant.id),
              item_name: product.item_name,
              item_brand: product.item_brand,
              item_category: product.item_category,
              item_variant: variantTitle,
              price,
            },
            { item_image_url: image, quantity: 1 }
          ),
        ],
      },
    });

    // keep config in sync for the next add_to_cart
    cfg.product = {
      ...product,
      item_id: variant.sku || String(variant.id),
      item_variant: variantTitle,
      item_image_url: image,
      price,
      sku: variant.sku || '',
    };
  }

  function bindVariantListener() {
    if (cfg.pageType !== 'product') return;

    let lastVariantId = null;

    document.addEventListener('variant:update', (event) => {
      const variant = event.detail?.resource;
      if (!variant || variant.id === lastVariantId) return;
      lastVariantId = variant.id;
      onVariantChange(variant);
    });
  }

  const ADD_SOURCES = new Set(['product-form-component', 'quick-add', 'cart-items-component']);

  function bindCartListener() {
    document.addEventListener('cart:update', (event) => {
      const data = event.detail?.data;
      if (!data || data.didError || !ADD_SOURCES.has(data.source)) return;

      const product = cfg.product;
      if (product) {
        trackAddToCart({
          masterProductId: product.masterProductId,
          item_id: product.item_id,
          item_name: product.item_name,
          item_brand: product.item_brand,
          item_category: product.item_category,
          item_variant: product.item_variant,
          item_image_url: product.item_image_url,
          price: product.price,
          quantity: data.itemCount || 1,
        });
        return;
      }

      if (data.productData) {
        trackAddToCart(data.productData);
        return;
      }

      if (data.productId) {
        trackAddToCart({
          masterProductId: data.productId,
          item_id: data.productId,
          item_name: '',
          price: 0,
          quantity: data.itemCount || 1,
        });
      }
    });
  }

  function firePageEvents() {
    switch (cfg.pageType) {
      case 'product':
        viewItem();
        break;
      case 'collection':
        viewItemList();
        break;
      case 'cart':
        viewCart();
        break;
    }
  }

  function init() {
    bindCartListener();
    bindCartErrorCapture();
    bindVariantListener();
    firePageEvents();
    debug('init', { pageType: cfg.pageType });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.gtmTrack = {
    addToCart: trackAddToCart,
    removeFromCart: trackRemoveFromCart,
    selectItem: trackSelectItem,
    purchase: trackPurchase,
    banner: trackBanner,
    bid: trackBid,
    viewItem,
    viewItemList,
    viewCart,
    variantChange: onVariantChange,
    pushCartError,
    captureCartErrors: scanCartErrors,
    debug,
  };
}
